import { Badge, Skeleton, Text, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@musa.tech/design-system';
import type { EnvironmentalMaterialTotal } from '@/hooks/useEnvironmentalData';
import { fmtWeightStr } from '@/utils/formatWeight';
import { getGroupConfig } from '@/utils/materialGroups';

function getGridCols(count: number): string {
  if (count === 1) return 'grid-cols-1 max-w-xs';
  if (count === 2) return 'grid-cols-1 sm:grid-cols-2';
  if (count === 3) return 'grid-cols-1 sm:grid-cols-3';
  return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';
}

interface WasteResumoCardProps {
  materialTotals: EnvironmentalMaterialTotal[];
  isLoading?: boolean;
  error?: unknown;
}

function aggregateByGroup(items: EnvironmentalMaterialTotal[]) {
  const map = new Map<string, {
    weight_collected: number;
    carbon_saved: number;
    breakdown: { name: string; weight: number }[];
  }>();

  for (const item of items) {
    const existing = map.get(item.group);
    if (existing) {
      existing.weight_collected += item.weight_collected;
      existing.carbon_saved += item.carbon_saved;
      existing.breakdown.push({ name: item.material.name, weight: item.weight_collected });
    } else {
      map.set(item.group, {
        weight_collected: item.weight_collected,
        carbon_saved: item.carbon_saved,
        breakdown: [{ name: item.material.name, weight: item.weight_collected }],
      });
    }
  }

  return Array.from(map.entries())
    .map(([group, totals]) => ({
      group,
      ...totals,
      breakdown: totals.breakdown.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    }))
    .sort((a, b) => b.weight_collected - a.weight_collected);
}

export const WasteResumoCard = ({ materialTotals, isLoading, error }: WasteResumoCardProps) => {
  const grouped = aggregateByGroup(materialTotals).filter((g) => g.carbon_saved > 0);
  const skeletonCount = grouped.length > 0 ? grouped.length : 3;

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <Text variant="heading" size="large" className="text-foreground">
            Resumo de Resíduos Coletados
          </Text>
          <Text variant="body" size="small" className="text-muted-foreground">
            Total coletado no período selecionado por tipo de resíduo
          </Text>
        </div>
      </div>

      {isLoading ? (
        <div className={`grid ${getGridCols(skeletonCount)} gap-4`}>
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-xl" />
          ))}
        </div>
      ) : error ? (
        <Text variant="body" size="small" className="text-error-500">
          Erro ao carregar dados: {String(error instanceof Error ? error.message : error)}
        </Text>
      ) : materialTotals.length === 0 ? (
        <Text variant="body" size="small" className="text-muted-foreground">
          Nenhum dado de resíduo encontrado para o período selecionado.
        </Text>
      ) : (
        <TooltipProvider>
          <div className={`grid ${getGridCols(grouped.length)} gap-4`}>
            {grouped.map((item) => {
              const config = getGroupConfig(item.group);
              const Icon = config.icon;
              const hasBreakdown = item.breakdown.length > 1;

              const card = (
                <div
                  className={`border border-primary-300 rounded-xl p-6 flex flex-col items-center text-center gap-2${hasBreakdown ? ' cursor-default' : ''}`}
                >
                  <div className="w-12 h-12 rounded-full bg-info-100 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-info-500" />
                  </div>
                  <Text variant="heading" className="text-2xl font-bold text-foreground">
                    {item.weight_collected > 0 ? fmtWeightStr(item.weight_collected) : '—'}
                  </Text>
                  <Text variant="label" size="small" className="font-medium text-foreground">
                    {config.label}
                  </Text>
                  {item.carbon_saved > 0 && (
                    <Badge
                      variant="success"
                      className="bg-success-100 text-success-700 border-success-100 text-[9px] px-2 py-0.5 rounded-full font-normal"
                    >
                      Evitou {fmtWeightStr(item.carbon_saved)} CO₂
                    </Badge>
                  )}
                </div>
              );

              if (!hasBreakdown) return <div key={item.group}>{card}</div>;

              return (
                <Tooltip key={item.group}>
                  <TooltipTrigger asChild>{card}</TooltipTrigger>
                  <TooltipContent side="top" className="space-y-1">
                    {item.breakdown.map((b) => (
                      <div key={b.name} className="flex justify-between gap-6 text-xs">
                        <span>{b.name}</span>
                        <span className="font-medium">{fmtWeightStr(b.weight)}</span>
                      </div>
                    ))}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </TooltipProvider>
      )}
    </div>
  );
};
